'use client';

import { motion } from 'motion/react';
import Image from 'next/image';
import Link from 'next/link';

import { ArticleFrontmatter } from '@/lib/types';
import { cn } from '@/lib/utils';

function formatEditorialDate(date: string) {
  return new Date(date)
    .toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
    .toUpperCase();
}

export function BlogJournal({
  articles,
  page,
  totalPages,
}: {
  articles: ArticleFrontmatter[];
  page: number;
  totalPages: number;
}) {
  const newerHref = page === 2 ? '/blog' : `/blog/page/${page - 1}`;
  const olderHref = `/blog/page/${page + 1}`;

  return (
    <section className="hero-padding">
      <div className="container">
        {/* Header */}
        <div className="mb-10 md:mb-12">
          <span className="text-text-tertiary font-mono text-xs font-medium tracking-[3px] uppercase">
            Capture
          </span>
          <h1 className="mt-4 text-3xl tracking-tight text-balance sm:text-4xl md:text-5xl lg:text-[64px]">
            Moments worth <span className="text-text-tertiary">keeping.</span>
          </h1>
        </div>

        {/* Editorial index */}
        <div className="border-foreground/10 border-t">
          {articles.map((article, i) => {
            const featured = page === 1 && i === 0;
            return (
              <motion.div
                key={article.slug}
                className={cn(
                  'border-foreground/10',
                  i < articles.length - 1 && 'border-b',
                )}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.5,
                  delay: Math.min(i, 5) * 0.05,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <Link
                  href={`/blog/${article.slug}`}
                  className={cn(
                    'group grid min-w-0 items-center gap-x-6 gap-y-3 pt-8 no-underline md:grid-cols-[150px_1fr_auto]',
                    i < articles.length - 1 && 'pb-8',
                  )}
                >
                  <time
                    dateTime={article.date}
                    className="text-text-tertiary group-hover:text-foreground self-start pt-1 font-mono text-xs tracking-[1px] uppercase transition-colors duration-300 md:pt-2"
                  >
                    {formatEditorialDate(article.date)}
                  </time>
                  <h2
                    className={cn(
                      'font-display text-foreground min-w-0 max-w-[600px] tracking-tight break-words transition-transform duration-300 group-hover:translate-x-2',
                      featured
                        ? 'text-2xl md:text-4xl md:leading-[44px]'
                        : 'text-xl md:text-[26px] md:leading-[33px]',
                    )}
                  >
                    {article.title}
                  </h2>
                  {article.image && (
                    <div
                      className={cn(
                        'relative shrink-0 overflow-hidden rounded-[10px]',
                        featured
                          ? 'h-[124px] w-[180px]'
                          : 'h-[92px] w-[132px]',
                      )}
                    >
                      <Image
                        src={article.image}
                        alt=""
                        fill
                        sizes="180px"
                        priority={featured}
                        className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    </div>
                  )}
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Pagination */}
        {(page > 1 || page < totalPages) && (
          <nav
            aria-label="Blog pagination"
            className="flex items-center justify-between pt-8"
          >
            {page > 1 ? (
              <Link
                href={newerHref}
                className="text-foreground text-sm font-medium no-underline transition-opacity hover:opacity-70"
              >
                ← Newer posts
              </Link>
            ) : (
              <span />
            )}
            {page < totalPages && (
              <Link
                href={olderHref}
                className="text-foreground text-sm font-medium no-underline transition-opacity hover:opacity-70"
              >
                Older posts →
              </Link>
            )}
          </nav>
        )}
      </div>
    </section>
  );
}
